<template>
  <b-container fluid class="marketplace-page my-4">
    <div v-if="getActiveAccount">
      <div class="page-header d-flex justify-content-between align-items-center flex-wrap mb-3">
        <div>
          <h1 class="mb-0"><i class="fas fa-store mr-2"></i>Marketplace</h1>
          <p class="text-muted mb-0">Buy and sell ZCO2 directly on-chain, priced in EURe, USDC or EURC.</p>
        </div>
        <div class="d-flex flex-wrap" style="gap: 8px;">
          <b-button v-if="isMarketplaceAdmin" variant="outline-secondary" @click="openAdminPanel">
            <i class="fas fa-shield-halved mr-1"></i>Admin
          </b-button>
          <b-button v-if="canCreateOffer" variant="primary" @click="openCreateModal">
            <i class="fas fa-plus mr-1"></i>Create Offer
          </b-button>
        </div>
      </div>

      <a href="https://uramp.gnosis.io" target="_blank" class="onramp-banner mb-4">
        <i class="fas fa-right-left mr-2"></i>
        Need EURe, USDC or EURC? Convert from euros/dollars via uRamp
        <i class="fas fa-arrow-up-right-from-square ml-2"></i>
      </a>

      <div class="row">
        <div class="col-lg-3 mb-4">
          <div class="filters-panel">
            <h5 class="mb-3">Filters</h5>

            <b-form-group label="Price range">
              <div class="d-flex" style="gap: 8px;">
                <b-form-input v-model="filters.minPrice" placeholder="Min" type="number" step="0.0001" min="0"></b-form-input>
                <b-form-input v-model="filters.maxPrice" placeholder="Max" type="number" step="0.0001" min="0"></b-form-input>
              </div>
            </b-form-group>

            <b-form-group label="Payment token">
              <b-form-select v-model="filters.paymentTokenSymbol" :options="tokenFilterOptions"></b-form-select>
            </b-form-group>

            <b-form-group label="Seller wallet">
              <b-form-input v-model="filters.sellerWallet" placeholder="0x..."></b-form-input>
            </b-form-group>

            <b-form-group label="Sort by">
              <b-form-select v-model="filters.sort" :options="sortOptions"></b-form-select>
            </b-form-group>

            <b-form-checkbox v-model="filters.onlyMine" switch class="mb-3">Only my offers</b-form-checkbox>

            <b-button block variant="primary" @click="applyFilters">Apply filters</b-button>
            <b-button block variant="outline-secondary" class="mt-2" @click="resetFilters">Reset</b-button>
          </div>
        </div>

        <div class="col-lg-9">
          <div v-if="loading" class="text-center text-muted py-5">Loading offers…</div>

          <div v-else-if="!offers.length" class="empty-state text-center py-5">
            <i class="fas fa-box-open mb-2"></i>
            <p class="mb-0">No offers match your filters yet.</p>
          </div>

          <div v-else class="offers-grid">
            <div v-for="offer in offers" :key="offer.id" class="offer-card" :class="{ 'is-cancelled': offer.status === 'cancelled' }">
              <div class="offer-card-top">
                <span class="token-badge">{{ offer.paymentTokenSymbol }}</span>
                <span v-if="offer.status === 'cancelled'" class="status-badge">Cancelled</span>
              </div>

              <div class="offer-price">
                {{ formatNumber(offer.pricePerToken) }} <small>{{ offer.paymentTokenSymbol }} / ZCO2</small>
              </div>

              <div class="offer-amount">
                <i class="fas fa-cubes mr-1"></i>{{ formatNumber(offer.remainingAmount) }} ZCO2 available
              </div>

              <div class="offer-seller">
                <i class="fas fa-wallet mr-1"></i>{{ truncateAddress(offer.sellerWallet) }}
              </div>

              <div class="offer-actions">
                <template v-if="isOwnOffer(offer)">
                  <b-button v-if="offer.status === 'active'" size="sm" variant="outline-secondary" @click="openEditModal(offer)">Edit</b-button>
                  <b-button v-if="offer.status === 'active'" size="sm" variant="outline-danger" :disabled="cancelling[offer.id]" @click="cancelOfferRow(offer)">
                    {{ cancelling[offer.id] ? 'Cancelling…' : 'Cancel' }}
                  </b-button>
                </template>
                <b-button v-else-if="offer.status === 'active' && offer.remainingAmount > 0" size="sm" variant="primary" @click="openBuyModal(offer)">
                  Buy
                </b-button>
              </div>
            </div>
          </div>

          <div ref="sentinel" class="d-flex justify-content-center mt-3" v-if="hasMore">
            <b-button size="sm" variant="outline-secondary" :disabled="loadingMore" @click="loadMore">
              {{ loadingMore ? 'Loading…' : 'Load more' }}
            </b-button>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="text-center py-5">
      <h1>Connect your wallet</h1>
      <p>You need to connect your Polygon wallet to browse and trade on the marketplace.</p>
      <connect-wallet-button class="mt-2" label="Connect"/>
    </div>

    <!-- Buy modal -->
    <b-modal id="buy-offer-modal" title="Buy ZCO2" @hidden="buyModalOffer = null">
      <div v-if="buyModalOffer">
        <p>Buying from <strong>{{ truncateAddress(buyModalOffer.sellerWallet) }}</strong> at
          <strong>{{ formatNumber(buyModalOffer.pricePerToken) }} {{ buyModalOffer.paymentTokenSymbol }}</strong> per ZCO2.</p>
        <b-form-group label="Amount of ZCO2 to buy">
          <b-input-group>
            <b-form-input v-model="buyAmount" type="number" min="0" :max="buyModalOffer.remainingAmount" step="0.0001"></b-form-input>
            <b-input-group-append>
              <b-button variant="secondary" @click="buyAmount = buyModalOffer.remainingAmount">Max</b-button>
            </b-input-group-append>
          </b-input-group>
          <small class="text-muted">Available: {{ formatNumber(buyModalOffer.remainingAmount) }} ZCO2</small>
        </b-form-group>
        <p class="total-line">Total: <strong>{{ formatNumber(buyTotal) }} {{ buyModalOffer.paymentTokenSymbol }}</strong></p>
      </div>
      <template #modal-footer="{ hide }">
        <b-button variant="secondary" @click="hide()">Cancel</b-button>
        <b-button variant="primary" :disabled="buying" @click="submitBuy">{{ buying ? 'Processing…' : 'Confirm purchase' }}</b-button>
      </template>
    </b-modal>

    <!-- Create offer modal -->
    <b-modal id="create-offer-modal" title="Create Offer" @hidden="resetCreateForm">
      <b-form-group label="ZCO2 amount">
        <b-form-input v-model="createForm.zco2Amount" type="number" min="0" step="0.0001"></b-form-input>
        <small class="text-muted">Your balance: {{ formatNumber(getTokenBalance) }} ZCO2</small>
      </b-form-group>
      <b-form-group label="Payment token">
        <b-form-select v-model="createForm.paymentTokenAddress" :options="paymentTokenOptions"></b-form-select>
      </b-form-group>
      <b-form-group label="Price per ZCO2">
        <b-form-input v-model="createForm.pricePerToken" type="number" min="0" step="0.0001"></b-form-input>
      </b-form-group>
      <template #modal-footer="{ hide }">
        <b-button variant="secondary" @click="hide()">Cancel</b-button>
        <b-button variant="primary" :disabled="creatingOffer" @click="submitCreateOffer">{{ creatingOffer ? 'Processing…' : 'Create offer' }}</b-button>
      </template>
    </b-modal>

    <!-- Edit offer modal -->
    <b-modal id="edit-offer-modal" title="Edit Offer" @hidden="editModalOffer = null">
      <div v-if="editModalOffer">
        <b-form-group label="Total ZCO2 amount (replaces remaining amount)">
          <b-form-input v-model="editForm.zco2Amount" type="number" min="0" step="0.0001"></b-form-input>
        </b-form-group>
        <b-form-group label="Payment token">
          <b-form-select v-model="editForm.paymentTokenAddress" :options="paymentTokenOptions"></b-form-select>
        </b-form-group>
        <b-form-group label="Price per ZCO2">
          <b-form-input v-model="editForm.pricePerToken" type="number" min="0" step="0.0001"></b-form-input>
        </b-form-group>
      </div>
      <template #modal-footer="{ hide }">
        <b-button variant="secondary" @click="hide()">Cancel</b-button>
        <b-button variant="primary" :disabled="savingEdit" @click="submitEdit">{{ savingEdit ? 'Processing…' : 'Save changes' }}</b-button>
      </template>
    </b-modal>

    <!-- Admin panel modal -->
    <b-modal id="admin-panel-modal" title="Marketplace Admin" size="lg">
      <b-tabs>
        <b-tab title="Fees">
          <b-form-group label="Fee percentage (basis points, 100 = 1%, max 2000 = 20%)">
            <b-form-input v-model.number="adminPanel.feeBps" type="number" min="0" max="2000"></b-form-input>
          </b-form-group>
          <b-form-group label="Flat fee (per trade, payment-token-agnostic units)">
            <b-form-input v-model="adminPanel.flatFee" type="number" min="0" step="0.0001"></b-form-input>
          </b-form-group>
          <b-button variant="primary" :disabled="adminPanel.savingFees" @click="saveFees">
            {{ adminPanel.savingFees ? 'Saving…' : 'Save fees' }}
          </b-button>

          <hr>
          <h6>Accrued fees</h6>
          <div v-for="pt in paymentTokens" :key="pt.id" class="accrued-row">
            <span>{{ pt.symbol }}: {{ formatNumber(adminPanel.accruedFees[pt.address] || 0) }}</span>
            <b-button size="sm" variant="outline-secondary" @click="withdrawAccruedFee(pt)">Withdraw</b-button>
          </div>
        </b-tab>

        <b-tab title="Payment Tokens">
          <div v-for="pt in paymentTokens" :key="pt.id" class="accrued-row">
            <span>{{ pt.symbol }} — {{ truncateAddress(pt.address) }}</span>
            <b-button size="sm" variant="outline-danger" @click="removePaymentTokenRow(pt)">Remove</b-button>
          </div>
          <hr>
          <b-form-group label="New payment token address">
            <b-input-group>
              <b-form-input v-model="adminPanel.newTokenAddress" placeholder="0x..."></b-form-input>
              <b-input-group-append>
                <b-button variant="secondary" @click="previewNewToken">Preview</b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
          <div v-if="adminPanel.newTokenPreview" class="token-preview mb-3">
            <p class="mb-1"><strong>{{ adminPanel.newTokenPreview.name }}</strong> ({{ adminPanel.newTokenPreview.symbol }}), {{ adminPanel.newTokenPreview.decimals }} decimals</p>
            <b-button size="sm" variant="primary" @click="confirmAddToken">Confirm add</b-button>
          </div>
        </b-tab>

        <b-tab title="Seller Whitelist">
          <b-form-checkbox v-model="adminPanel.whitelistEnabled" switch class="mb-3" @change="toggleWhitelistMode">
            Whitelist enabled (only whitelisted wallets can create offers)
          </b-form-checkbox>

          <div v-for="w in adminPanel.whitelistWallets" :key="w.id" class="accrued-row">
            <span>{{ truncateAddress(w.wallet) }}<span v-if="w.label" class="text-muted"> — {{ w.label }}</span></span>
            <b-button size="sm" variant="outline-danger" @click="removeWhitelistRow(w)">Remove</b-button>
          </div>
          <hr>
          <b-input-group class="mb-2">
            <b-form-input v-model="adminPanel.newWhitelistWallet" placeholder="0x..."></b-form-input>
            <b-form-input v-model="adminPanel.newWhitelistLabel" placeholder="Label (optional)"></b-form-input>
            <b-input-group-append>
              <b-button variant="secondary" @click="addWhitelistRow">Add</b-button>
            </b-input-group-append>
          </b-input-group>
        </b-tab>
      </b-tabs>
      <template #modal-footer="{ hide }">
        <b-button variant="secondary" @click="hide()">Close</b-button>
      </template>
    </b-modal>
  </b-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { ethers } from 'ethers'
import Swal from 'sweetalert2'
import Offer from '../api/Offer'
import Order from '../api/Order'
import PaymentToken from '../api/PaymentToken'
import ConnectWalletButton from '../components/structure/ConnectWalletButton.vue'

// ============================================================================
// TEMPORARY DEMO DATA — for an in-person presentation only.
// Purely client-side: these offers are never sent to the backend and never
// touch the smart contract or real ZCO2. Buy/Edit/Cancel on a demo offer
// (offer.isDemo === true) is short-circuited below to only mutate this local
// array, never calling the chain or the API.
// To remove: delete this block, the two `isDemo`-guard branches in
// submitBuy/submitEdit/cancelOfferRow, and the `mergeDemoOffers` call+method.
// ============================================================================
const DEMO_OFFERS = [
  { id: 'demo-1', onchainOfferId: 'demo-1', sellerWallet: '0x8f3Cc1B4a9E27d6F52a1C9a0b7De441F2C9a7A3e', paymentTokenAddress: 'demo-eure', paymentTokenSymbol: 'EURe', pricePerToken: 12.5, remainingAmount: 340, status: 'active', isDemo: true },
  { id: 'demo-2', onchainOfferId: 'demo-2', sellerWallet: '0x1Ab6De4f9C2a7B3e8F0d5C1a9E6b3D2F7A4c8E1b', paymentTokenAddress: 'demo-usdc', paymentTokenSymbol: 'USDC', pricePerToken: 13.75, remainingAmount: 85, status: 'active', isDemo: true },
  { id: 'demo-3', onchainOfferId: 'demo-3', sellerWallet: '0xC4e2A7f1B9d3E6c8A0f4D2b7C1e9A3f6D8b2E5c0', paymentTokenAddress: 'demo-eure', paymentTokenSymbol: 'EURe', pricePerToken: 11.9, remainingAmount: 1200, status: 'active', isDemo: true },
  { id: 'demo-4', onchainOfferId: 'demo-4', sellerWallet: '0x7Bd1F4a8C2e6D9b3A0f7C5e1B4d8A2f6C9e3D0b7', paymentTokenAddress: 'demo-usdc', paymentTokenSymbol: 'USDC', pricePerToken: 14.2, remainingAmount: 50, status: 'active', isDemo: true },
  { id: 'demo-5', onchainOfferId: 'demo-5', sellerWallet: '0x2Fa9C6d3B7e1A4f8D0c2E5b9A3f7C1e4D8b6A0f2', paymentTokenAddress: 'demo-eure', paymentTokenSymbol: 'EURe', pricePerToken: 12.1, remainingAmount: 610, status: 'active', isDemo: true }
]

export default {
  name: 'Marketplace',
  components: { ConnectWalletButton },

  data() {
    return {
      offers: [],
      loading: false,
      loadingMore: false,
      page: 1,
      pageSize: 12,
      hasMore: false,
      paymentTokens: [],
      filters: {
        minPrice: '',
        maxPrice: '',
        sellerWallet: '',
        paymentTokenSymbol: '',
        sort: 'newest',
        onlyMine: false
      },
      canCreateOffer: false,
      isMarketplaceAdmin: false,
      createForm: { zco2Amount: '', paymentTokenAddress: '', pricePerToken: '' },
      creatingOffer: false,
      buyModalOffer: null,
      buyAmount: '',
      buying: false,
      editModalOffer: null,
      editForm: { zco2Amount: '', paymentTokenAddress: '', pricePerToken: '' },
      savingEdit: false,
      cancelling: {},
      adminPanel: {
        feeBps: 0,
        flatFee: '0',
        accruedFees: {},
        newTokenAddress: '',
        newTokenPreview: null,
        whitelistEnabled: true,
        whitelistWallets: [],
        newWhitelistWallet: '',
        newWhitelistLabel: '',
        savingFees: false
      },
      observer: null
    }
  },

  computed: {
    ...mapGetters('accounts', ['getActiveAccount']),
    ...mapGetters('user', ['getWeb2ActiveAccount']),
    ...mapGetters('tokenContract', ['getTokenBalance']),
    ...mapGetters('marketplaceContract', ['getMarketplaceContract']),
    tokenFilterOptions() {
      return [{ value: '', text: 'All tokens' }].concat(this.paymentTokens.map((t) => ({ value: t.symbol, text: t.symbol })))
    },
    paymentTokenOptions() {
      return this.paymentTokens.map((t) => ({ value: t.address, text: t.symbol }))
    },
    sortOptions() {
      return [
        { value: 'newest', text: 'Newest' },
        { value: 'oldest', text: 'Oldest' },
        { value: 'price_asc', text: 'Price: low to high' },
        { value: 'price_desc', text: 'Price: high to low' }
      ]
    },
    buyTotal() {
      const qty = parseFloat(this.buyAmount) || 0
      return this.buyModalOffer ? qty * this.buyModalOffer.pricePerToken : 0
    }
  },

  watch: {
    getActiveAccount(newVal) {
      if (newVal) this.init()
    },
    getMarketplaceContract(newVal) {
      if (newVal) this.checkPermissions()
    }
  },

  created() {
    if (this.getActiveAccount) this.init()
  },

  mounted() {
    this.observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && this.hasMore && !this.loadingMore) this.loadMore()
    })
  },

  beforeDestroy() {
    if (this.observer) this.observer.disconnect()
  },

  updated() {
    if (this.observer && this.$refs.sentinel) this.observer.observe(this.$refs.sentinel)
  },

  methods: {
    ...mapActions('marketplaceContract', [
      'createOffer', 'modifyOffer', 'cancelOffer', 'buyOffer', 'hasAdminRole', 'setFees', 'withdrawFees',
      'getAccruedFees', 'addPaymentToken', 'removePaymentToken', 'fetchTokenMetadata',
      'isWhitelistEnabled', 'isWhitelistedSeller', 'setWhitelistEnabled', 'addToWhitelist', 'removeFromWhitelist'
    ]),

    init() {
      this.loadPaymentTokens()
      this.applyFilters()
      this.checkPermissions()
    },

    async checkPermissions() {
      if (!this.getMarketplaceContract || !this.getActiveAccount) return
      try {
        const [whitelistEnabled, whitelisted, isAdmin] = await Promise.all([
          this.isWhitelistEnabled(),
          this.isWhitelistedSeller(this.getActiveAccount),
          this.hasAdminRole(this.getActiveAccount)
        ])
        this.canCreateOffer = !whitelistEnabled || whitelisted
        this.isMarketplaceAdmin = isAdmin && this.getWeb2ActiveAccount && this.getWeb2ActiveAccount.role.name === 'Administrator'
        this.adminPanel.whitelistEnabled = whitelistEnabled
      } catch (e) {
        console.error(e)
      }
    },

    loadPaymentTokens() {
      PaymentToken.getPaymentTokens().then((res) => {
        this.paymentTokens = res.data
        if (!this.createForm.paymentTokenAddress && this.paymentTokens.length) {
          this.createForm.paymentTokenAddress = this.paymentTokens[0].address
        }
      }).catch(console.error)
    },

    buildQuery(page) {
      const q = {
        page, pageSize: this.pageSize, sort: this.filters.sort
      }
      if (this.filters.minPrice !== '') q.minPrice = this.filters.minPrice
      if (this.filters.maxPrice !== '') q.maxPrice = this.filters.maxPrice
      if (this.filters.sellerWallet) q.sellerWallet = this.filters.sellerWallet
      if (this.filters.paymentTokenSymbol) q.paymentTokenSymbol = this.filters.paymentTokenSymbol
      if (this.filters.onlyMine && this.getActiveAccount) {
        q.mine = 'true'
        q.wallet = this.getActiveAccount
      }
      return q
    },

    applyFilters() {
      this.loading = true
      this.page = 1
      Offer.getOffers(this.buildQuery(1)).then((res) => {
        this.offers = this.mergeDemoOffers(res.data)
        this.hasMore = res.meta ? res.meta.hasMore : false
      }).catch(console.error).finally(() => { this.loading = false })
    },

    // TEMPORARY — see DEMO_OFFERS above. Only prepended on the first page,
    // and only while they still match the active price/token/seller filters,
    // so filtering behaves consistently whether a card is real or fake.
    mergeDemoOffers(realOffers) {
      const matching = DEMO_OFFERS.filter((o) => {
        if (this.filters.paymentTokenSymbol && o.paymentTokenSymbol !== this.filters.paymentTokenSymbol) return false
        if (this.filters.minPrice !== '' && o.pricePerToken < parseFloat(this.filters.minPrice)) return false
        if (this.filters.maxPrice !== '' && o.pricePerToken > parseFloat(this.filters.maxPrice)) return false
        if (this.filters.sellerWallet && !o.sellerWallet.toLowerCase().includes(this.filters.sellerWallet.toLowerCase())) return false
        if (this.filters.onlyMine) return false
        return true
      })
      return matching.concat(realOffers)
    },

    resetFilters() {
      this.filters = { minPrice: '', maxPrice: '', sellerWallet: '', paymentTokenSymbol: '', sort: 'newest', onlyMine: false }
      this.applyFilters()
    },

    loadMore() {
      if (this.loadingMore || !this.hasMore) return
      this.loadingMore = true
      const nextPage = this.page + 1
      Offer.getOffers(this.buildQuery(nextPage)).then((res) => {
        this.offers = this.offers.concat(res.data)
        this.page = nextPage
        this.hasMore = res.meta ? res.meta.hasMore : false
      }).catch(console.error).finally(() => { this.loadingMore = false })
    },

    isOwnOffer(offer) {
      return this.getActiveAccount && offer.sellerWallet && offer.sellerWallet.toLowerCase() === this.getActiveAccount.toLowerCase()
    },

    truncateAddress(addr) {
      if (!addr) return ''
      return addr.substring(0, 7) + '...' + addr.substring(addr.length - 5)
    },

    formatNumber(n) {
      const num = parseFloat(n) || 0
      return new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 4 }).format(num)
    },

    tokenByAddress(address) {
      return this.paymentTokens.find((t) => t.address.toLowerCase() === (address || '').toLowerCase())
    },

    openCreateModal() {
      this.$bvModal.show('create-offer-modal')
    },
    resetCreateForm() {
      this.createForm = { zco2Amount: '', paymentTokenAddress: this.paymentTokens[0] ? this.paymentTokens[0].address : '', pricePerToken: '' }
    },
    async submitCreateOffer() {
      const amount = parseFloat(this.createForm.zco2Amount)
      const price = parseFloat(this.createForm.pricePerToken)
      if (!amount || amount <= 0) { Swal.fire('Error', 'Enter a valid ZCO2 amount', 'error'); return }
      if (!price || price <= 0) { Swal.fire('Error', 'Enter a valid price', 'error'); return }
      const token = this.tokenByAddress(this.createForm.paymentTokenAddress)
      if (!token) { Swal.fire('Error', 'Select a payment token', 'error'); return }

      this.creatingOffer = true
      try {
        const zco2AmountWei = ethers.utils.parseEther(this.createForm.zco2Amount.toString())
        const pricePerTokenWei = ethers.utils.parseUnits(this.createForm.pricePerToken.toString(), token.decimals)

        const { tx, offerId } = await this.createOffer({
          zco2AmountWei, paymentTokenAddress: token.address, pricePerTokenWei
        })

        if (offerId !== null) {
          await Offer.createOffer({ onchainOfferId: offerId, txHash: tx.hash })
        }

        this.$bvModal.hide('create-offer-modal')
        this.$toasted.show('Offer created', { type: 'success', duration: 5000, theme: 'bubble', position: 'top-center' })
        this.applyFilters()
      } catch (e) {
        Swal.fire('Error', e.message, 'error')
      } finally {
        this.creatingOffer = false
      }
    },

    openBuyModal(offer) {
      this.buyModalOffer = offer
      this.buyAmount = ''
      this.$bvModal.show('buy-offer-modal')
    },
    async submitBuy() {
      const qty = parseFloat(this.buyAmount)
      if (!qty || qty <= 0 || qty > this.buyModalOffer.remainingAmount) {
        Swal.fire('Error', 'Enter a valid amount within the available balance', 'error')
        return
      }

      // TEMPORARY demo path — a demo offer never touches the chain or the
      // backend, it just updates this local card so the flow looks real.
      if (this.buyModalOffer.isDemo) {
        this.buying = true
        await new Promise((resolve) => setTimeout(resolve, 700))
        this.buyModalOffer.remainingAmount -= qty
        this.$bvModal.hide('buy-offer-modal')
        this.$toasted.show('Purchase completed', { type: 'success', duration: 5000, theme: 'bubble', position: 'top-center' })
        this.buying = false
        return
      }

      const token = this.tokenByAddress(this.buyModalOffer.paymentTokenAddress)
      if (!token) { Swal.fire('Error', 'Unknown payment token', 'error'); return }

      this.buying = true
      try {
        const zco2AmountWei = ethers.utils.parseEther(this.buyAmount.toString())
        const pricePerTokenWei = ethers.utils.parseUnits(this.buyModalOffer.pricePerToken.toString(), token.decimals)
        const paymentAmountWei = zco2AmountWei.mul(pricePerTokenWei).div(ethers.utils.parseEther('1'))

        const { tx } = await this.buyOffer({
          offerId: this.buyModalOffer.onchainOfferId,
          zco2AmountWei,
          paymentTokenAddress: token.address,
          paymentAmountWei
        })

        await Order.createOrder({ txHash: tx.hash })
        await Offer.syncOffer(this.buyModalOffer.id)

        this.$bvModal.hide('buy-offer-modal')
        this.$toasted.show('Purchase completed', { type: 'success', duration: 5000, theme: 'bubble', position: 'top-center' })
        this.applyFilters()
      } catch (e) {
        Swal.fire('Error', e.message, 'error')
      } finally {
        this.buying = false
      }
    },

    openEditModal(offer) {
      this.editModalOffer = offer
      this.editForm = {
        zco2Amount: offer.remainingAmount,
        paymentTokenAddress: offer.paymentTokenAddress,
        pricePerToken: offer.pricePerToken
      }
      this.$bvModal.show('edit-offer-modal')
    },
    async submitEdit() {
      const newAmount = parseFloat(this.editForm.zco2Amount)
      const price = parseFloat(this.editForm.pricePerToken)
      if (isNaN(newAmount) || newAmount < 0) { Swal.fire('Error', 'Enter a valid amount', 'error'); return }
      if (!price || price <= 0) { Swal.fire('Error', 'Enter a valid price', 'error'); return }

      // TEMPORARY demo path — see submitBuy above.
      if (this.editModalOffer.isDemo) {
        this.savingEdit = true
        await new Promise((resolve) => setTimeout(resolve, 700))
        this.editModalOffer.remainingAmount = newAmount
        this.editModalOffer.paymentTokenAddress = this.editForm.paymentTokenAddress
        this.editModalOffer.paymentTokenSymbol = this.tokenByAddress(this.editForm.paymentTokenAddress)?.symbol || this.editModalOffer.paymentTokenSymbol
        this.editModalOffer.pricePerToken = price
        this.$bvModal.hide('edit-offer-modal')
        this.$toasted.show('Offer updated', { type: 'success', duration: 5000, theme: 'bubble', position: 'top-center' })
        this.savingEdit = false
        return
      }

      const token = this.tokenByAddress(this.editForm.paymentTokenAddress)
      if (!token) { Swal.fire('Error', 'Select a payment token', 'error'); return }

      this.savingEdit = true
      try {
        const newAmountWei = ethers.utils.parseEther(this.editForm.zco2Amount.toString())
        const newPricePerTokenWei = ethers.utils.parseUnits(this.editForm.pricePerToken.toString(), token.decimals)
        const isIncrease = newAmount > this.editModalOffer.remainingAmount

        await this.modifyOffer({
          offerId: this.editModalOffer.onchainOfferId,
          newAmountWei, newPaymentTokenAddress: token.address, newPricePerTokenWei, isIncrease
        })
        await Offer.syncOffer(this.editModalOffer.id)

        this.$bvModal.hide('edit-offer-modal')
        this.$toasted.show('Offer updated', { type: 'success', duration: 5000, theme: 'bubble', position: 'top-center' })
        this.applyFilters()
      } catch (e) {
        Swal.fire('Error', e.message, 'error')
      } finally {
        this.savingEdit = false
      }
    },

    async cancelOfferRow(offer) {
      const confirm = await Swal.fire({ title: 'Cancel offer?', text: 'The remaining ZCO2 will be returned to your wallet.', showCancelButton: true, confirmButtonText: 'Cancel offer' })
      if (!confirm.isConfirmed) return

      // TEMPORARY demo path — see submitBuy above.
      if (offer.isDemo) {
        this.$set(this.cancelling, offer.id, true)
        await new Promise((resolve) => setTimeout(resolve, 500))
        offer.status = 'cancelled'
        this.$toasted.show('Offer cancelled', { type: 'success', duration: 5000, theme: 'bubble', position: 'top-center' })
        this.$set(this.cancelling, offer.id, false)
        return
      }

      this.$set(this.cancelling, offer.id, true)
      try {
        await this.cancelOffer(offer.onchainOfferId)
        await Offer.cancelSyncOffer(offer.id)
        this.$toasted.show('Offer cancelled', { type: 'success', duration: 5000, theme: 'bubble', position: 'top-center' })
        this.applyFilters()
      } catch (e) {
        Swal.fire('Error', e.message, 'error')
      } finally {
        this.$set(this.cancelling, offer.id, false)
      }
    },

    openAdminPanel() {
      this.loadAdminData()
      this.$bvModal.show('admin-panel-modal')
    },
    async loadAdminData() {
      try {
        const [feeBps, flatFee] = await Promise.all([
          this.getMarketplaceContract.feeBps(),
          this.getMarketplaceContract.flatFee()
        ])
        this.adminPanel.feeBps = Number(feeBps)
        this.adminPanel.flatFee = ethers.utils.formatEther(flatFee)

        const accruedFees = {}
        for (const pt of this.paymentTokens) {
          const raw = await this.getAccruedFees(pt.address)
          accruedFees[pt.address] = parseFloat(ethers.utils.formatUnits(raw, pt.decimals))
        }
        this.adminPanel.accruedFees = accruedFees

        const wl = await PaymentToken.getSellerWhitelist()
        this.adminPanel.whitelistWallets = wl.data
      } catch (e) {
        console.error(e)
      }
    },
    async saveFees() {
      this.adminPanel.savingFees = true
      try {
        const flatFeeWei = ethers.utils.parseEther((this.adminPanel.flatFee || '0').toString())
        await this.setFees({ feeBps: this.adminPanel.feeBps, flatFeeWei })
        this.$toasted.show('Fees updated', { type: 'success', duration: 4000, theme: 'bubble', position: 'top-center' })
      } catch (e) {
        Swal.fire('Error', e.message, 'error')
      } finally {
        this.adminPanel.savingFees = false
      }
    },
    async withdrawAccruedFee(pt) {
      const { value: amount } = await Swal.fire({
        title: `Withdraw ${pt.symbol} fees`,
        input: 'number',
        inputLabel: `Available: ${this.adminPanel.accruedFees[pt.address] || 0}`,
        showCancelButton: true
      })
      if (!amount) return
      try {
        const amountRaw = ethers.utils.parseUnits(amount.toString(), pt.decimals)
        await this.withdrawFees({ tokenAddress: pt.address, to: this.getActiveAccount, amountRaw })
        this.$toasted.show('Withdrawn', { type: 'success', duration: 4000, theme: 'bubble', position: 'top-center' })
        this.loadAdminData()
      } catch (e) {
        Swal.fire('Error', e.message, 'error')
      }
    },
    async previewNewToken() {
      if (!this.adminPanel.newTokenAddress) return
      try {
        this.adminPanel.newTokenPreview = await this.fetchTokenMetadata(this.adminPanel.newTokenAddress)
      } catch (e) {
        Swal.fire('Error', 'Could not read token metadata — check the address', 'error')
      }
    },
    async confirmAddToken() {
      try {
        await this.addPaymentToken(this.adminPanel.newTokenAddress)
        await PaymentToken.createPaymentToken({
          address: this.adminPanel.newTokenAddress.toLowerCase(),
          symbol: this.adminPanel.newTokenPreview.symbol,
          name: this.adminPanel.newTokenPreview.name,
          decimals: this.adminPanel.newTokenPreview.decimals,
          active: true
        })
        this.adminPanel.newTokenAddress = ''
        this.adminPanel.newTokenPreview = null
        this.loadPaymentTokens()
        this.$toasted.show('Payment token added', { type: 'success', duration: 4000, theme: 'bubble', position: 'top-center' })
      } catch (e) {
        Swal.fire('Error', e.message, 'error')
      }
    },
    async removePaymentTokenRow(pt) {
      const confirm = await Swal.fire({ title: `Remove ${pt.symbol}?`, showCancelButton: true })
      if (!confirm.isConfirmed) return
      try {
        await this.removePaymentToken(pt.address)
        await PaymentToken.deletePaymentToken(pt.id)
        this.loadPaymentTokens()
      } catch (e) {
        Swal.fire('Error', e.message, 'error')
      }
    },
    async toggleWhitelistMode(enabled) {
      try {
        await this.setWhitelistEnabled(enabled)
        this.checkPermissions()
      } catch (e) {
        Swal.fire('Error', e.message, 'error')
        this.adminPanel.whitelistEnabled = !enabled
      }
    },
    async addWhitelistRow() {
      if (!this.adminPanel.newWhitelistWallet) return
      try {
        await this.addToWhitelist(this.adminPanel.newWhitelistWallet)
        await PaymentToken.addToWhitelist(this.adminPanel.newWhitelistWallet.toLowerCase(), this.adminPanel.newWhitelistLabel)
        this.adminPanel.newWhitelistWallet = ''
        this.adminPanel.newWhitelistLabel = ''
        this.loadAdminData()
      } catch (e) {
        Swal.fire('Error', e.message, 'error')
      }
    },
    async removeWhitelistRow(w) {
      try {
        await this.removeFromWhitelist(w.wallet)
        await PaymentToken.removeFromWhitelist(w.id)
        this.loadAdminData()
      } catch (e) {
        Swal.fire('Error', e.message, 'error')
      }
    }
  }
}
</script>

<style scoped lang="scss">
.onramp-banner {
  display: block;
  padding: 12px 18px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(79,140,74,0.15), rgba(21,152,149,0.1));
  border: 1px solid rgba(var(--accent-green-rgb), 0.3);
  color: var(--text-main);
  text-decoration: none;
  font-weight: 600;

  &:hover {
    color: var(--accent-green);
    text-decoration: none;
  }
}

.filters-panel {
  position: sticky;
  top: 16px;
  background: var(--card-bg);
  border: 1px solid var(--border-subtle);
  border-radius: 14px;
  padding: 18px;
}

.offers-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
}

.offer-card {
  background: var(--card-bg);
  border: 1px solid var(--border-subtle);
  border-radius: 14px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: border-color 0.18s ease, transform 0.15s ease;

  &:hover {
    border-color: var(--accent-green);
    transform: translateY(-2px);
  }

  &.is-cancelled {
    opacity: 0.55;
  }
}

.offer-card-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.token-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  background: rgba(var(--accent-green-rgb), 0.15);
  color: var(--accent-green);
  font-weight: 700;
  font-size: 0.78rem;
}

.status-badge {
  font-size: 0.72rem;
  color: var(--text-muted);
}

.offer-price {
  font-size: 1.4rem;
  font-weight: 800;
  color: var(--text-heading);

  small {
    font-size: 0.75rem;
    font-weight: 500;
    color: var(--text-muted);
  }
}

.offer-amount, .offer-seller {
  font-size: 0.88rem;
  color: var(--text-muted);
}

.offer-actions {
  margin-top: 8px;
  display: flex;
  gap: 8px;
}

.empty-state {
  color: var(--text-muted);

  i {
    font-size: 2rem;
  }
}

.accrued-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid var(--border-subtle);
}

.token-preview {
  padding: 10px 14px;
  border-radius: 10px;
  background: var(--bg-elevated);
  border: 1px solid var(--border-subtle);
}

.total-line {
  font-size: 1.1rem;
}
</style>
